export type BrandMode = 'neutral' | 'aspis';

export type TenantAuthProvider = 'emailPassword' | 'google' | 'microsoft' | 'saml';

export interface TenantSignupPolicy {
  allowSelfSignup: boolean;
  requireEmailVerification: boolean;
  requireManagerApproval: boolean;
  inviteOnly: boolean;
  allowedEmailDomains: string[];
}

export interface TenantDomainProvisioning {
  mode: 'shared' | 'managedSubdomain';
  setupSubdomain?: string;
  verificationStatus: 'draft' | 'pending' | 'verified';
}

export interface TenantOnboardingConfig {
  enabledProviders: TenantAuthProvider[];
  signupPolicy: TenantSignupPolicy;
  domainProvisioning: TenantDomainProvisioning;
  staffImportEnabled: boolean;
  selfServeSetupEnabled: boolean;
}

import {
  DispatchAdapter,
  DispatchIntegrationProfile,
  ProviderArchetype,
  ServiceLine,
} from '../types/platform';
import { PROVIDER_CAPABILITY_PROFILES } from './capabilities';

export interface BrandingConfig {
  productName: string;
  shortName: string;
  legalAttribution: string;
  logoUrl: string;
}

export const DEFAULT_LEGAL_ATTRIBUTION = 'Configured by your organization';

export type TenantEnvironmentStage = 'demo' | 'staging' | 'production';

export interface TenantModuleFlags {
  coreOps: boolean;
  clinicalOps: boolean;
  fleetAndStock: boolean;
  dispatch: boolean;
  analytics: boolean;
  majorIncident: boolean;
  trustCenter: boolean;
}

export type QuickStartBundle = 'core_ops' | 'clinical_ops' | 'full_ops' | 'custom';

export interface TenantOperationalChecklistItem {
  id: string;
  title: string;
  completed: boolean;
  requiredForModules?: Array<keyof TenantModuleFlags>;
  notes?: string;
}

export interface TenantModuleConfigGuards {
  enforceOperationalChecklist: boolean;
  blockSensitiveModulesInDemo: boolean;
  sensitiveModules: Array<keyof TenantModuleFlags>;
}

export interface TenantQuickStartConfig {
  enabled: boolean;
  bundle: QuickStartBundle;
  demoRestricted: boolean;
  managedSubdomain?: string;
  onboarding?: TenantOnboardingConfig;
  operationalChecklist?: TenantOperationalChecklistItem[];
  configGuards?: TenantModuleConfigGuards;
  lastConfiguredAt?: string;
  lastConfiguredBy?: string;
  conversion?: {
    convertedAt?: string;
    convertedBy?: string;
    checklist?: string[];
  };
}

export interface TenantIdentifierPolicy {
  employeePrefix?: string;
  employeePattern?: string;
  incidentPrefix?: string;
  incidentPattern?: string;
  eprPrefix?: string;
  eprPattern?: string;
  linkEprToIncident?: boolean;
}

/**
 * A tenant-defined named role group. Maps to a base Role for hierarchy/access evaluation
 * but surfaces a custom label, color and responsibilities across the platform.
 */
export interface TenantRoleGroup {
  id: string;               // URL-safe slug, e.g. "senior-paramedic"
  label: string;            // Display name, e.g. "Senior Paramedic"
  baseRole: string;         // Role enum value — used for hierarchy checks
  color: TenantRoleGroupColor;
  description: string;
  responsibilities: string[];
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
}

export interface TenantDispatchPolicy {
  autoAllocateP1: boolean;
  ackSlaMinutes: number;
  responseWaitCutoffMinutes: number;
  areaBoundary: {
    enabled: boolean;
    centerLat: number;
    centerLng: number;
    radiusKm: number;
  };
}

export interface TenantDrivingPolicy {
  maxContinuousMinutes: number;
  minRestMinutes: number;
  emergencyButtonVisibleWhenMoving: boolean;
}

export interface TenantClinicalPolicy {
  requireHandoverBeforeClear: boolean;
  defaultConveyanceDestinationMode: 'HospitalFirst' | 'ClinicalTriage' | 'NearestSafe';
  requireReferralReviewForSafeguarding: boolean;
}

export interface TenantRotaPolicy {
  maxWeeklyHours: number;
  mandatoryBreakAfterHours: number;
}

export interface TenantPolicyDelegation {
  dispatchOwnerRoleGroupId?: string;
  clinicalOwnerRoleGroupId?: string;
  rotaOwnerRoleGroupId?: string;
  hrOwnerRoleGroupId?: string;
  financeOwnerRoleGroupId?: string;
}

export interface TenantPolicyConfig {
  dispatch: TenantDispatchPolicy;
  driving: TenantDrivingPolicy;
  clinical: TenantClinicalPolicy;
  rota: TenantRotaPolicy;
  delegation: TenantPolicyDelegation;
  updatedAt?: string;
  updatedBy?: string;
}

export type TenantRoleGroupColor =
  | 'slate' | 'red' | 'amber' | 'teal'
  | 'blue' | 'purple' | 'rose' | 'emerald' | 'cyan' | 'indigo';

/** Maps a TenantRoleGroupColor token to full Tailwind badge class strings. */
export const ROLE_GROUP_COLOR_BADGE: Record<TenantRoleGroupColor, string> = {
  slate:   'bg-slate-50   border-slate-200  text-slate-700   dark:bg-slate-900/30  dark:border-slate-700  dark:text-slate-300',
  red:     'bg-red-50     border-red-200    text-red-700     dark:bg-red-900/30    dark:border-red-800    dark:text-red-300',
  amber:   'bg-amber-50   border-amber-200  text-amber-700   dark:bg-amber-900/30  dark:border-amber-800  dark:text-amber-300',
  teal:    'bg-teal-50    border-teal-200   text-teal-700    dark:bg-teal-900/30   dark:border-teal-800   dark:text-teal-300',
  blue:    'bg-blue-50    border-blue-200   text-blue-700    dark:bg-blue-900/30   dark:border-blue-800   dark:text-blue-300',
  purple:  'bg-purple-50  border-purple-200 text-purple-700  dark:bg-purple-900/30 dark:border-purple-800 dark:text-purple-300',
  rose:    'bg-rose-50    border-rose-200   text-rose-700    dark:bg-rose-900/30   dark:border-rose-800   dark:text-rose-300',
  emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-800 dark:text-emerald-300',
  cyan:    'bg-cyan-50    border-cyan-200   text-cyan-700    dark:bg-cyan-900/30   dark:border-cyan-800   dark:text-cyan-300',
  indigo:  'bg-indigo-50  border-indigo-200 text-indigo-700  dark:bg-indigo-900/30 dark:border-indigo-800 dark:text-indigo-300',
};

export interface TenantConfig {
  tenantId: string;
  displayName: string;
  timezone: string;
  locale: string;
  supportEmail: string;
  safeguardingReferralEmail: string;
  gpReferralEmail: string;
  badgePrefix: string;
  incidentPrefix: string;
  identifierPolicy?: TenantIdentifierPolicy;
  providerArchetype: ProviderArchetype;
  serviceLines: ServiceLine[];
  dispatchIntegration: DispatchIntegrationProfile;
  supportsMutualAid: boolean;
  eprDispatchSyncMode: 'always' | 'untilManualEdit';
  brandMode: BrandMode;
  branding: BrandingConfig;
  environmentStage: TenantEnvironmentStage;
  modules: TenantModuleFlags;
  quickStart: TenantQuickStartConfig;
  policy: TenantPolicyConfig;
  /** Custom role groups defined by the tenant admin. */
  roleGroups?: TenantRoleGroup[];
}

export interface TenantProfileOverrides {
  displayName?: string;
  supportEmail?: string;
  safeguardingReferralEmail?: string;
  gpReferralEmail?: string;
  badgePrefix?: string;
  incidentPrefix?: string;
  identifierPolicy?: Partial<TenantIdentifierPolicy>;
  eprDispatchSyncMode?: 'always' | 'untilManualEdit';
  dispatchIntegration?: Partial<DispatchIntegrationProfile>;
  branding?: Partial<BrandingConfig>;
  environmentStage?: TenantEnvironmentStage;
  modules?: Partial<TenantModuleFlags>;
  quickStart?: Partial<TenantQuickStartConfig>;
  policy?: Partial<TenantPolicyConfig>;
}

const defaultLogos: Record<BrandMode, string> = {
  neutral: '/assets/logo-aspis.svg',
  aspis: '/assets/logo-aspis.svg',
};

export const DEFAULT_BRAND_LOGO_URL = defaultLogos.aspis;

const brandMode = (import.meta.env.VITE_APP_BRAND_MODE as BrandMode) || 'neutral';
const safeBrandMode: BrandMode = brandMode === 'neutral' ? 'neutral' : 'aspis';

const envLogo = import.meta.env.VITE_APP_LOGO_URL?.trim();
const envProviderArchetype = (import.meta.env.VITE_PROVIDER_ARCHETYPE as ProviderArchetype) || 'MIXED_RESPONSE';
const providerArchetype: ProviderArchetype =
  PROVIDER_CAPABILITY_PROFILES[envProviderArchetype] ? envProviderArchetype : 'MIXED_RESPONSE';

const envServiceLines = import.meta.env.VITE_PROVIDER_SERVICE_LINES
  ? import.meta.env.VITE_PROVIDER_SERVICE_LINES.split(',').map((line) => line.trim()).filter(Boolean)
  : [];

const fallbackServiceLines = PROVIDER_CAPABILITY_PROFILES[providerArchetype].defaultServiceLines;
const serviceLines: ServiceLine[] =
  envServiceLines.length > 0 ? (envServiceLines as ServiceLine[]) : fallbackServiceLines;

const envDispatchAdapter = (import.meta.env.VITE_DISPATCH_ADAPTER as DispatchAdapter) || 'NATIVE';
const dispatchAdapter: DispatchAdapter = ['NATIVE', 'GOODSAM', 'HYBRID', 'EXTERNAL_API'].includes(envDispatchAdapter)
  ? envDispatchAdapter
  : 'NATIVE';

const envStage = (import.meta.env.VITE_ENV_STAGE as TenantEnvironmentStage) || 'production';
const environmentStage: TenantEnvironmentStage = ['demo', 'staging', 'production'].includes(envStage)
  ? envStage
  : 'production';

const envModule = (name: string, fallback: boolean): boolean => {
  const raw = import.meta.env[name as keyof ImportMetaEnv] as string | undefined;
  if (typeof raw !== 'string') return fallback;
  return raw.toLowerCase() === 'true';
};

const isAllowedBrandingLogoUrl = (value: string): boolean => {
  if (!value) return false;
  if (value.startsWith('/')) return true;
  if (value.startsWith('https://')) return true;
  if (value.startsWith('data:image/')) return true;
  return false;
};

export const normalizeBranding = (branding: Partial<BrandingConfig> | undefined, fallback: BrandingConfig): BrandingConfig => {
  const productName = branding?.productName?.trim();
  const shortName = branding?.shortName?.trim();
  const legalAttribution = branding?.legalAttribution?.trim();
  const logoUrl = branding?.logoUrl?.trim();

  return {
    productName: productName || fallback.productName,
    shortName: shortName || fallback.shortName,
    legalAttribution: legalAttribution || fallback.legalAttribution || DEFAULT_LEGAL_ATTRIBUTION,
    logoUrl: logoUrl && isAllowedBrandingLogoUrl(logoUrl)
      ? logoUrl
      : fallback.logoUrl || DEFAULT_BRAND_LOGO_URL,
  };
};

const defaultModules: TenantModuleFlags = {
  coreOps: envModule('VITE_MODULE_CORE_OPS', true),
  clinicalOps: envModule('VITE_MODULE_CLINICAL_OPS', true),
  fleetAndStock: envModule('VITE_MODULE_FLEET_STOCK', true),
  dispatch: envModule('VITE_MODULE_DISPATCH', true),
  analytics: envModule('VITE_MODULE_ANALYTICS', true),
  majorIncident: envModule('VITE_MODULE_MAJOR_INCIDENT', true),
  trustCenter: envModule('VITE_MODULE_TRUST_CENTER', true),
};

export const defaultTenantConfig: TenantConfig = {
  tenantId: import.meta.env.VITE_DEFAULT_TENANT_ID || 'tenant-default',
  displayName: import.meta.env.VITE_DEFAULT_TENANT_NAME || 'Operations Workspace',
  timezone: import.meta.env.VITE_DEFAULT_TENANT_TIMEZONE || 'Europe/London',
  locale: import.meta.env.VITE_DEFAULT_TENANT_LOCALE || 'en-GB',
  supportEmail: import.meta.env.VITE_SUPPORT_EMAIL || 'support@platform.local',
  safeguardingReferralEmail:
    import.meta.env.VITE_SAFEGUARDING_EMAIL || 'safeguarding@platform.local',
  gpReferralEmail: import.meta.env.VITE_GP_REFERRAL_EMAIL || 'referrals@platform.local',
  badgePrefix: import.meta.env.VITE_BADGE_PREFIX || 'ID',
  incidentPrefix: import.meta.env.VITE_INCIDENT_PREFIX || 'INC',
  identifierPolicy: {
    employeePrefix: import.meta.env.VITE_BADGE_PREFIX || 'ID',
    employeePattern: import.meta.env.VITE_EMPLOYEE_ID_PATTERN || 'YYMMXXXX',
    incidentPrefix: import.meta.env.VITE_INCIDENT_PREFIX || 'INC',
    incidentPattern: import.meta.env.VITE_INCIDENT_ID_PATTERN || 'YYYYMMDD-XXXX',
    eprPrefix: import.meta.env.VITE_EPR_PREFIX || import.meta.env.VITE_INCIDENT_PREFIX || 'INC',
    eprPattern: import.meta.env.VITE_EPR_ID_PATTERN || import.meta.env.VITE_INCIDENT_ID_PATTERN || 'YYYYMMDD-XXXX',
    linkEprToIncident: (import.meta.env.VITE_LINK_EPR_TO_INCIDENT || 'true').toLowerCase() === 'true',
  },
  providerArchetype,
  serviceLines,
  supportsMutualAid: (import.meta.env.VITE_SUPPORTS_MUTUAL_AID || 'true').toLowerCase() === 'true',
  dispatchIntegration: {
    adapter: dispatchAdapter,
    externalSystemName: import.meta.env.VITE_DISPATCH_EXTERNAL_SYSTEM_NAME || undefined,
    inboundSigningSecret: import.meta.env.VITE_DISPATCH_WEBHOOK_SECRET || undefined,
    supportsInboundAlerts: (import.meta.env.VITE_DISPATCH_SUPPORTS_INBOUND_ALERTS || 'true').toLowerCase() === 'true',
    supportsAcknowledgementSync: (import.meta.env.VITE_DISPATCH_SUPPORTS_ACK_SYNC || 'true').toLowerCase() === 'true',
    outboundSyncEnabled: (import.meta.env.VITE_DISPATCH_OUTBOUND_SYNC_ENABLED || 'false').toLowerCase() === 'true',
    outboundSyncEndpoint: import.meta.env.VITE_DISPATCH_OUTBOUND_SYNC_ENDPOINT || undefined,
    outboundSyncAuthToken: import.meta.env.VITE_DISPATCH_OUTBOUND_SYNC_TOKEN || undefined,
    outboundSyncRetrySeconds: Number.parseInt(import.meta.env.VITE_DISPATCH_OUTBOUND_SYNC_RETRY_SECONDS || '30', 10),
    autoAllocateC1Enabled: (import.meta.env.VITE_DISPATCH_AUTO_ALLOCATE_C1 || 'false').toLowerCase() === 'true',
    notes:
      import.meta.env.VITE_DISPATCH_INTEGRATION_NOTES ||
      'Designed for private provider operations, including GoodSAM or other external alerting where configured.',
  },
  eprDispatchSyncMode:
    ((import.meta.env.VITE_EPR_DISPATCH_SYNC_MODE as 'always' | 'untilManualEdit') || 'always') ===
    'untilManualEdit'
      ? 'untilManualEdit'
      : 'always',
  brandMode: safeBrandMode,
  branding: {
    productName: safeBrandMode === 'neutral' ? 'Operations Workspace' : 'Aspis',
    shortName: safeBrandMode === 'neutral' ? 'Workspace' : 'Aspis',
    legalAttribution:
      import.meta.env.VITE_LEGAL_ATTRIBUTION ||
      DEFAULT_LEGAL_ATTRIBUTION,
    logoUrl: envLogo || defaultLogos[safeBrandMode],
  },
  environmentStage,
  modules: defaultModules,
  quickStart: {
    enabled: false,
    bundle: 'custom',
    demoRestricted: false,
    onboarding: {
      enabledProviders: ['emailPassword'],
      signupPolicy: {
        allowSelfSignup: false,
        requireEmailVerification: true,
        requireManagerApproval: true,
        inviteOnly: true,
        allowedEmailDomains: [],
      },
      domainProvisioning: {
        mode: 'shared',
        verificationStatus: 'draft',
      },
      staffImportEnabled: false,
      selfServeSetupEnabled: false,
    },
  },
  policy: {
    dispatch: {
      autoAllocateP1: (import.meta.env.VITE_DISPATCH_AUTO_ALLOCATE_C1 || 'false').toLowerCase() === 'true',
      ackSlaMinutes: Number.parseInt(import.meta.env.VITE_DISPATCH_ACK_SLA_MINUTES || '2', 10),
      responseWaitCutoffMinutes: Number.parseInt(import.meta.env.VITE_DISPATCH_RESPONSE_WAIT_CUTOFF_MINUTES || '15', 10),
      areaBoundary: {
        enabled: false,
        centerLat: 51.5072,
        centerLng: -0.1276,
        radiusKm: 2,
      },
    },
    driving: {
      maxContinuousMinutes: Number.parseInt(import.meta.env.VITE_DRIVING_MAX_CONTINUOUS_MINUTES || '120', 10),
      minRestMinutes: Number.parseInt(import.meta.env.VITE_DRIVING_MIN_REST_MINUTES || '15', 10),
      emergencyButtonVisibleWhenMoving: false,
    },
    clinical: {
      requireHandoverBeforeClear: true,
      defaultConveyanceDestinationMode: 'HospitalFirst',
      requireReferralReviewForSafeguarding: true,
    },
    rota: {
      maxWeeklyHours: 48,
      mandatoryBreakAfterHours: 6,
    },
    delegation: {},
  },
};

export const mergeTenantConfig = (
  base: TenantConfig,
  overrides?: TenantProfileOverrides | null
): TenantConfig => {
  if (!overrides) return base;

  return {
    ...base,
    displayName: overrides.displayName || base.displayName,
    supportEmail: overrides.supportEmail || base.supportEmail,
    safeguardingReferralEmail:
      overrides.safeguardingReferralEmail || base.safeguardingReferralEmail,
    gpReferralEmail: overrides.gpReferralEmail || base.gpReferralEmail,
    badgePrefix: overrides.badgePrefix || base.badgePrefix,
    incidentPrefix: overrides.incidentPrefix || base.incidentPrefix,
    identifierPolicy: {
      ...(base.identifierPolicy || {}),
      ...((overrides.identifierPolicy as Partial<TenantIdentifierPolicy> | undefined) || {}),
    },
    eprDispatchSyncMode: overrides.eprDispatchSyncMode || base.eprDispatchSyncMode,
    dispatchIntegration: {
      ...base.dispatchIntegration,
      ...(overrides.dispatchIntegration || {}),
    },
    environmentStage: overrides.environmentStage || base.environmentStage,
    branding: normalizeBranding(overrides.branding, base.branding),
    modules: {
      ...base.modules,
      ...(overrides.modules || {}),
    },
    quickStart: {
      ...base.quickStart,
      ...(overrides.quickStart || {}),
      configGuards: {
        ...(base.quickStart.configGuards || {
          enforceOperationalChecklist: false,
          blockSensitiveModulesInDemo: false,
          sensitiveModules: ['dispatch', 'clinicalOps', 'fleetAndStock'],
        }),
        ...((overrides.quickStart && overrides.quickStart.configGuards) || {}),
      },
      operationalChecklist:
        (overrides.quickStart && overrides.quickStart.operationalChecklist) ||
        base.quickStart.operationalChecklist ||
        [],
      conversion: {
        ...(base.quickStart.conversion || {}),
        ...((overrides.quickStart && overrides.quickStart.conversion) || {}),
      },
    },
    policy: {
      ...base.policy,
      ...(overrides.policy || {}),
      dispatch: {
        ...base.policy.dispatch,
        ...((overrides.policy && overrides.policy.dispatch) || {}),
        areaBoundary: {
          ...base.policy.dispatch.areaBoundary,
          ...((overrides.policy && overrides.policy.dispatch && overrides.policy.dispatch.areaBoundary) || {}),
        },
      },
      driving: {
        ...base.policy.driving,
        ...((overrides.policy && overrides.policy.driving) || {}),
      },
      clinical: {
        ...base.policy.clinical,
        ...((overrides.policy && overrides.policy.clinical) || {}),
      },
      rota: {
        ...base.policy.rota,
        ...((overrides.policy && overrides.policy.rota) || {}),
      },
      delegation: {
        ...base.policy.delegation,
        ...((overrides.policy && overrides.policy.delegation) || {}),
      },
    },
  };
};
